import React, { useMemo, memo } from 'react'
import { Line } from 'react-chartjs-2'
import moment from 'moment'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js'
import { formatNumberEng } from '../helper/formatNumber'
import {
  DASHBOARD_TIME_RANGE,
  BUYER_ORDER_UNPAID,
  HOST_ORDER_VERIFIED,
  HOST_ORDER_COLLECTED,
  HOST_ORDER_SUBMITTED_COLLECTED,
  HOST_ORDER_SUBMITTED_VERIFIED,
  COLOR_PANEL,
  BUYER_ORDER_PAID
} from '../../../utils/constant'
import useDashboardRedirect from '../hooks/useDashboardRedirect'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler)

const formatTrendLabel = (dateStr) => {
  if (!dateStr) return ''

  if (dateStr.includes('-W')) {
    const parts = dateStr.split('-W')
    return `W${parts[1]}`
  }

  const parts = dateStr.split('-')
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}`
  }

  if (parts.length === 2) {
    const monthNum = parseInt(parts[1], 10)
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]
    return months[monthNum - 1] || ''
  }

  return dateStr
}

const DURATION_CONFIG = {
  DAYS: 7,
  WEEKS: 62,
}

const LOOP_STEP = {
  NEXT: 1,
  FORCE_EXIT: 2,
}

const revenueLabelPlugin = {
  id: 'revenueLabelPlugin',
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    ctx.save()

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (meta.hidden) return

      meta.data.forEach((element, index) => {
        const value = dataset.data[index]
        if (value === undefined || value === null || value === 0) return

        const { x, y } = element.tooltipPosition()
        const marginBottom = 6

        ctx.fillStyle = COLOR_PANEL.GREEN
        ctx.font = '600 9px Inter, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'

        const label = `$${formatNumberEng(value, 2)}`
        ctx.fillText(label, x, y - marginBottom)
      })
    })

    ctx.restore()
  },
}

function SalesAndFulfilment({ revenueReport, loading, filters, isViewAll, eventTimeRange }) {
  const { redirectToManageOrders } = useDashboardRedirect()

  const handleClickStage = (stage) => {
    if (isViewAll) return
    redirectToManageOrders({
      paymentStatus: stage.paymentStatus,
      dateRangeOrder: { start: filters?.start, end: filters?.end },
      dateRangeEvent: eventTimeRange,
      eventId: filters?.eid,
      status: filters?.status,
    })
  }

  const trendData = useMemo(() => {
    const rawTrend = revenueReport?.revenueTrend || []
    if (!filters || !filters.start || !filters.end) return rawTrend

    const start = moment.unix(filters.start)
    const end = moment.unix(filters.end)
    const dataMap = new Map(rawTrend.map((d) => [d.date, d.revenue]))
    const filledData = []

    const current = moment(start)
    let unit = 'days'
    let formatStr = 'YYYY-MM-DD'

    if (filters.time === DASHBOARD_TIME_RANGE.THIS_WEEK) {
      unit = 'days'
      formatStr = 'YYYY-MM-DD'
    } else if (filters.time === DASHBOARD_TIME_RANGE.THIS_MONTH) {
      unit = 'weeks'
      formatStr = 'GGGG-[W]WW'
    } else if (filters.time === DASHBOARD_TIME_RANGE.DAYS_90) {
      unit = 'months'
      formatStr = 'YYYY-MM'
    } else {
      const diffDays = Math.ceil((filters.end - filters.start) / (24 * 60 * 60))
      if (diffDays <= DURATION_CONFIG.DAYS) {
        unit = 'days'
        formatStr = 'YYYY-MM-DD'
      } else if (diffDays <= DURATION_CONFIG.WEEKS) {
        unit = 'weeks'
        formatStr = 'GGGG-[W]WW'
      } else {
        unit = 'months'
        formatStr = 'YYYY-MM'
      }
    }

    while (current.isSameOrBefore(end, unit)) {
      const key = current.format(formatStr)
      filledData.push({
        date: key,
        revenue: dataMap.get(key) || 0,
      })
      const addValue = current.isSame(end, unit) ? LOOP_STEP.FORCE_EXIT : LOOP_STEP.NEXT
      current.add(addValue, unit)
    }

    return filledData
  }, [revenueReport?.revenueTrend, filters])

  const N = trendData.length

  const revenuePoints = useMemo(() => {
    let result = {
      text: 'No revenue data',
      isDown: false,
    }

    if (N > 0) {
      const latest = trendData[N - 1]
      let arrow = '↑'
      let isDown = false
      if (N > 1) {
        const prev = trendData[N - 2]
        if (latest.revenue < prev.revenue) {
          arrow = '↓'
          isDown = true
        }
      }

      result = {
        text: `${arrow} ${formatNumberEng(latest.revenue, 2)} SGD`,
        isDown,
      }
    }
    return result
  }, [trendData, N])

  const lineData = useMemo(() => {
    const pointBackgrounds = trendData.map((_, i) =>
      (i === N - 1 ? COLOR_PANEL.GREEN : COLOR_PANEL.WHITE))

    const pointBorders = trendData.map((_, i) =>
      (i === N - 1 ? COLOR_PANEL.WHITE : COLOR_PANEL.GREEN))

    const pointRadius = trendData.map((_, i) => (i === N - 1 ? 5 : 3.5))

    const pointHoverRadius = trendData.map((_, i) => (i === N - 1 ? 6 : 4.5))

    return {
      labels: trendData.map((d) => formatTrendLabel(d.date)),
      datasets: [
        {
          data: trendData.map((d) => d.revenue),
          borderColor: COLOR_PANEL.GREEN,
          backgroundColor: `${COLOR_PANEL.GREEN}1f`,
          fill: true,
          tension: 0,
          borderWidth: 2.5,
          pointRadius,
          pointBackgroundColor: pointBackgrounds,
          pointBorderColor: pointBorders,
          pointBorderWidth: 2,
          pointHoverRadius,
          pointHoverBackgroundColor: pointBackgrounds,
          pointHoverBorderColor: pointBorders,
          pointHoverBorderWidth: 2,
        },
      ],
    }
  }, [trendData, N])

  const lineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (isViewAll) return
      if (!elements.length) return
      const { index } = elements[0]
      const selectedPoint = trendData[index]
      if (!selectedPoint) return

      let startTimestamp
      let endTimestamp

      if (selectedPoint.date.includes('-W')) {
        const startOfWeek = moment(selectedPoint.date, 'GGGG-[W]WW').startOf('isoWeek')
        const endOfWeek = moment(selectedPoint.date, 'GGGG-[W]WW').endOf('isoWeek')
        startTimestamp = startOfWeek.unix()
        endTimestamp = endOfWeek.unix()
      } else {
        const parts = selectedPoint.date.split('-')
        if (parts.length === 3) {
          const currentDate = moment(selectedPoint.date, 'YYYY-MM-DD')
          const prevDate = moment(currentDate).subtract(1, 'days')
          startTimestamp = prevDate.startOf('day').unix()
          endTimestamp = currentDate.endOf('day').unix()
        } else if (parts.length === 2) {
          const currentMonth = moment(selectedPoint.date, 'YYYY-MM')
          const prevMonth = moment(currentMonth).subtract(1, 'months')
          startTimestamp = prevMonth.startOf('month').unix()
          endTimestamp = currentMonth.endOf('month').unix()
        }
      }

      if (startTimestamp && endTimestamp) {
        redirectToManageOrders({
          dateRangeOrder: { start: startTimestamp, end: endTimestamp },
          dateRangeEvent: eventTimeRange,
          eventId: filters?.eid,
          status: filters?.status,
        })
      }
    },
    onHover: (event, elements) => {
      const target = event.native?.target || event.target
      if (target) {
        target.style.cursor = (elements.length && !isViewAll) ? 'pointer' : 'default'
      }
    },
    layout: {
      padding: {
        top: 22,
        bottom: 0,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label(context) {
            return ` $${formatNumberEng(context.parsed.y, 2)} SGD`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: COLOR_PANEL.FAINT,
          font: {
            size: 9,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: COLOR_PANEL.GRID,
          drawBorder: false,
          borderDash: [5, 5],
        },
        ticks: {
          display: false,
        },
      },
    },
  }), [trendData, redirectToManageOrders, isViewAll])

  const { stagesToRender, totalOrders, collectedPct } = useMemo(() => {
    const statusData = revenueReport?.fulfillmentStatus || {}
    const collectedCount = Number(statusData.collected) || 0
    const notCollectedCount = Number(statusData.notCollected) || 0

    const total = collectedCount + notCollectedCount

    const getPctVal = (count) => {
      if (total === 0) return 0
      return Math.round((count / total) * 100)
    }

    const cPct = getPctVal(collectedCount)
    const ncPct = getPctVal(notCollectedCount)

    const stages = [
      {
        id: 'collected',
        name: 'Collected',
        color: COLOR_PANEL.TEAL,
        count: collectedCount,
        displayPct: String(cPct),
        barPct: cPct,
        paymentStatus: [
          { value: HOST_ORDER_COLLECTED, label: 'Collected', type: 'host', includeSubmitted: HOST_ORDER_SUBMITTED_COLLECTED },
        ],
      },
      {
        id: 'not_collected',
        name: 'Not Collected',
        color: COLOR_PANEL.AMBER,
        count: notCollectedCount,
        displayPct: String(ncPct),
        barPct: ncPct,
        paymentStatus: [
          { value: HOST_ORDER_VERIFIED, label: 'Verified', type: 'host', includeSubmitted: HOST_ORDER_SUBMITTED_VERIFIED },
          { value: BUYER_ORDER_UNPAID, label: 'Not Paid' },
          { value: BUYER_ORDER_PAID, label: 'Pending Review', isPending: true }
        ],
      }
    ]

    return {
      stagesToRender: stages,
      totalOrders: total,
      collectedPct: cPct
    }
  }, [revenueReport?.fulfillmentStatus])

  return (
    <div className='revenue-fulfilment-section'>
      <div className='section-label' style={{ marginTop: '18px' }}>
        Sales &amp; Fulfilment
      </div>
      <div className='chart-grid' style={{ marginBottom: 0 }}>
        {/* Revenue trend line chart */}
        <div className='chart-card' style={{ minWidth: 0 }}>
          <div className='chart-header'>
            <div>
              <div className='chart-title'>Sales Trend</div>
              <div className='chart-sub'>Sales collected (SGD)</div>
            </div>
            {loading ? (
              <span className='skeleton-loader' style={{ width: '100px', height: '16px' }} />
            ) : (
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: revenuePoints.isDown ? COLOR_PANEL.RED : COLOR_PANEL.GREEN,
                }}
              >
                {/* {revenuePoints.text} */}
              </div>
            )}
          </div>
          {loading ? (
            <div
              className='skeleton-loader'
              style={{
                width: '100%',
                height: '130px',
                borderRadius: '8px',
                marginTop: '10px',
              }}
            />
          ) : (
            <div style={{ height: '130px', position: 'relative', width: '100%', minWidth: 0 }}>
              <Line data={lineData} options={lineOptions} plugins={[revenueLabelPlugin]} />
            </div>
          )}
        </div>

        {/* Fulfilment horizontal bars */}
        <div className='chart-card'>
          <div className='chart-header'>
            <div>
              <div className='chart-title'>Fulfilment Status</div>
              <div className='chart-sub'>Orders collected vs outstanding</div>
            </div>
          </div>
          {loading ? (
            <div className='stages-list loading'>
              {[1, 2].map((_, i) => (
                <div key={i} className='stage-item'>
                  <div className='stage-info'>
                    <span className='skeleton-loader' style={{ width: '80px', height: '12px' }} />
                    <span className='skeleton-loader' style={{ width: '40px', height: '12px' }} />
                  </div>
                  <div className='skeleton-loader stage-progress-bg' style={{ width: '100%' }} />
                </div>
              ))}
            </div>
          ) : (
            <div className='stages-list'>
              {stagesToRender.map((stage, idx) => (
                <div
                  key={idx}
                  className={`stage-item ${isViewAll ? 'disabled-stage' : ''}`}
                  onClick={() => handleClickStage(stage)}
                >
                  <div className='stage-info'>
                    <span className='stage-name'>{stage.name}</span>
                    <span className='stage-value'>
                      {stage.count} of {totalOrders} ({stage.displayPct}%)
                    </span>
                  </div>
                  <div className='stage-progress-bg'>
                    <div
                      className='stage-progress-fill'
                      style={{
                        width: `${stage.barPct}%`,
                        background: stage.color,
                      }}
                    ></div>
                  </div>
                </div>
              ))}

              <div className='stage-summary'>
                <span className='summary-total'>
                  {totalOrders} total orders
                </span>
                <span className='summary-pct'>
                  {collectedPct}% collected
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(SalesAndFulfilment)
